import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MuiTooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { useState } from 'react';

interface InfoTipProps {
  /** The term being defined. Names the button for assistive tech: "About {term}". */
  term: string;
  /** What the term means. Plain text, one or two sentences. */
  definition: string;
  className?: string;
}

/**
 * An info icon that explains the term beside it.
 *
 * Built on MUI's Tooltip rather than `componentsv2/Tooltip`, whose default
 * skin is a hard-coded white box that does not follow the dark theme. This one
 * uses the semantic surface tokens, so it reads correctly in both.
 *
 * It opens on hover and keyboard focus like any tooltip, and also on click,
 * because a definition has to be reachable on a phone, where there is no
 * hover. MUI's own touch listener is disabled on purpose: it opens on
 * touchstart and closes again 1.5s later, too short to read a sentence, and it
 * would race the click handler. A tap opens the tip; a tap elsewhere, Escape,
 * or moving focus away closes it.
 *
 * The icon is small, but the button is a 48px square (44 is not on the spacing
 * scale) pulled back with negative margins, so it meets the touch-target size
 * without making the row it sits in any taller.
 */
export default function InfoTip({ term, definition, className }: InfoTipProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span className={clsx('inline-flex', className)}>
        <MuiTooltip
          title={definition}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disableTouchListener
          describeChild
          arrow
          placement="top"
          // `!` because MUI's own tooltip skin sets every one of these, and
          // whether Tailwind or Emotion wins otherwise depends on stylesheet
          // order: the app injects MUI first, Storybook does not.
          slotProps={{
            tooltip: {
              className:
                '!max-w-[32rem] !rounded-md border border-surface-border !bg-surface-elevated !p-12 text-left !font-normal !text-on-surface shadow-2 !body-2',
            },
            arrow: { className: '!text-surface-elevated' },
          }}
        >
          <button
            type="button"
            aria-label={`About ${term}`}
            onClick={() => setOpen(true)}
            className="-mx-14 -my-14 inline-flex h-48 w-48 shrink-0 items-center justify-center rounded-full text-16 text-on-surface-muted hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            <InfoOutlined fontSize="inherit" aria-hidden />
          </button>
        </MuiTooltip>
      </span>
    </ClickAwayListener>
  );
}
